// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '../../../../../common-utils/libraries/tailwind/utils';
// Store & actions
import actionsContracts from '../../api/actionsContracts';
// Components
import HeaderTitle from '../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '../../../../../common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '../../../../../common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '../../../../../common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsContract } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.contractId
 * @param root0.merchantAccountId
 * @param root0.label
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalUpdateLabel
 */
function ModalUpdateLabel({ isOpen, setIsOpen, contractId, merchantAccountId, label, setLaunchRefetch }) {
    // Toast
    const { toast } = useToast();

    // Form
    const formSchema = z.object({
        label: z.string()
            .nonempty()
            .min(1, '')
            .max(100, '')
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { label: label || '' }
    });

    // State
    const [showSubmitButton, setShowSubmitButton] = useState(true);

    // Api calss
    const onSuccess = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_label.success' namespace={nsContract} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_label.error' namespace={nsContract} />,
            variant: 'danger'
        });
    };
    const submitUpdateLabel = actionsContracts.updateLabel(onSuccess, onError);

    // Functions
    const onSubmit = (data) => {
        submitUpdateLabel.mutate({ contractAccessId: merchantAccountId, contractId: contractId, label: data.label });
    };

    const onClose = () => {
        setIsOpen(false);
        if (submitUpdateLabel.isSuccess) {
            setLaunchRefetch(true);
        }
    };

    return <Modal open={isOpen} onOpenChange={onClose}>
        <ModalContent className={cn('max-w-lg')}>
            <HeaderTitle title='modal.update_label.title' namespace={nsContract} />
            <ModalBody>
                <Form {...form} onSubmit={form.handleSubmit(onSubmit)}>
                    <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.update_label.text' namespace={nsContract} />
                    </FormLabel>
                    <FormField
                        name='label'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        entity='modal.update_label.text'
                                        namespace={nsContract}
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </Form>
            </ModalBody>
            <FooterForm
                onSubmit={form.handleSubmit(onSubmit)}
                isSubmitting={submitUpdateLabel.isLoading}
                showSubmitButton={showSubmitButton}
                isSubmitDisabled={!form.formState.isValid}
            />
        </ModalContent>
    </Modal>;
}

ModalUpdateLabel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    contractId: PropTypes.string.isRequired,
    merchantAccountId: PropTypes.string.isRequired,
    label: PropTypes.string,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default ModalUpdateLabel;